import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUpOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { average, groupSumByMonth, topN } from '@/lib/dashboardAggregations';
import type { ProductionOrderRegisterRow, ProductionYieldRow } from '@/types/reports';

export default function ProductionSlide() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: productionOrders = [] } = useQuery({
    queryKey: ['reports', 'production-order-register'],
    queryFn: async () => (await apiClient.get<ProductionOrderRegisterRow[]>('/reports/production-order-register')).data,
    refetchInterval: 60_000,
  });
  const { data: productionYield = [] } = useQuery({
    queryKey: ['reports', 'production-yield'],
    queryFn: async () => (await apiClient.get<ProductionYieldRow[]>('/reports/production-yield')).data,
    refetchInterval: 60_000,
  });

  const totalOrders = useMemo(() => new Set(productionOrders.map((r) => r.productionOrderId)).size, [productionOrders]);
  const producedQty = useMemo(() => productionOrders.reduce((sum, r) => sum + r.producedQty, 0), [productionOrders]);
  const avgYield = useMemo(() => average(productionYield.map((r) => r.yieldPercent).filter((v): v is number => v != null)), [productionYield]);
  const rejectedQty = useMemo(() => productionYield.reduce((sum, r) => sum + r.rejectedQty, 0), [productionYield]);

  const plannedVsProduced = useMemo(
    () => groupSumByMonth(productionOrders, (r) => r.plannedDate, [
      { key: 'planned', value: (r) => r.plannedQty },
      { key: 'produced', value: (r) => r.producedQty },
    ]),
    [productionOrders],
  );
  const topYieldOrders = useMemo(() => topN(productionYield, (r) => r.producedQty, 5), [productionYield]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <TvKpiTile label={t('production.totalOrders')} value={totalOrders} icon={<PrecisionManufacturingIcon />} accentColor={colors.production} />
        <TvKpiTile label={t('production.producedQty')} value={producedQty.toLocaleString()} icon={<Inventory2Icon />} accentColor={colors.production} />
        <TvKpiTile label={t('production.avgYield')} value={avgYield > 0 ? `${avgYield.toFixed(1)}%` : '—'} icon={<TrendingUpIcon />} accentColor={colors.production} />
        <TvKpiTile label={t('production.rejectedQty')} value={rejectedQty.toLocaleString()} icon={<CancelOutlinedIcon />} accentColor={colors.adjustments} />
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 2, minHeight: 0 }}>
        <ChartCard title={t('production.plannedVsProducedTitle')} subtitle={t('production.plannedVsProducedSubtitle')} height={420}>
          <DashboardBarChart
            categories={plannedVsProduced.map((m) => m.month)}
            series={[
              { label: tr('columns.plannedQty'), data: plannedVsProduced.map((m) => m.values.planned), color: colors.procurement },
              { label: tr('columns.producedQty'), data: plannedVsProduced.map((m) => m.values.produced), color: colors.production },
            ]}
            height={420}
            legendPosition="bottom"
          />
        </ChartCard>
        <TvTopList
          title={t('production.yieldByOrderTitle')}
          accentColor={colors.production}
          items={topYieldOrders.map((r) => ({ label: r.orderNumber, value: r.yieldPercent ?? 0, formattedValue: r.yieldPercent != null ? `${r.yieldPercent.toFixed(1)}%` : '—' }))}
        />
      </Box>
    </Box>
  );
}
